import { useMemo } from 'react';
import { X } from 'lucide-react';
import { JsonPeekPopover } from './JsonPeekPopover';
import { getJsonPathHint } from './jsonFilterFieldsValidator';
import { useMssqlCompatLevel, useMssqlCompatRecheck } from '../../hooks/useMssqlCompatLevel';
import { cn } from '../../../../../lib/utils';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { JsonStructureCachePathType } from '../../../../../types/electron';
import type { FilterGroup } from '../../../core/types/workspaceTypes';

export type JsonPathFieldProps = {
  /** Current JSON-path string (e.g. `$.customer_id`). */
  value: string;
  /** Fired on each freetext edit and on a pick from the peek list. */
  onChange: (v: string) => void;
  /**
   * Optional — fires *only* on tree picks (not on freetext typing) and
   * carries the observed leaf type, so the chip can auto-default a
   * type-appropriate sub-op.
   */
  onTreePick?: (v: string, type: JsonStructureCachePathType | undefined) => void;
  /** Optional — fires when the user picks the popover's whole-column footer link. */
  onPickDocumentScope?: (
    sub: 'is_valid' | 'is_not_valid' | 'is_empty' | 'is_not_empty',
  ) => void;
  /**
   * Filter scope this picker represents. `'path'` = the trigger shows
   * the path string (mono); `'whole-column'` = the trigger shows
   * "whole column" with a `{}` icon. The chip drives this based on
   * whether the active sub-op is document-level. When `scope ===
   * 'whole-column'`, the picker still owns the round-trip back to path
   * mode: clicking it reopens Peek; picking a tree row clears scope.
   */
  scope?: 'path' | 'whole-column';
  /**
   * Optional — fires when the user clicks the small `×` clear control on
   * the picker. The chip resets `path` to `''` and the active sub-op
   * back to a path-mode default (e.g. `has_property`).
   */
  onClear?: () => void;
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId;
  /**
   * Whether the source column is JSON-declared-on-a-text-typed column
   * (engine-specific). Drives the validity guard added to the server-scan
   * SQL inside the popover. Defaults to `false` (native JSON storage).
   */
  declaredOnText?: boolean;
  /**
   * Applied Table Explorer filters (minus this chip's own group) to scope
   * the Peek popover's scan to — filter-aware JSON peek (Path A). Optional;
   * omission preserves today's whole-column peek behavior.
   */
  scanFilters?: FilterGroup[];
  /**
   * When true (default), the popover surfaces the "use [*] to match array
   * elements" tip. Hosts where the wildcard hint isn't relevant
   * (loose-FK target picker — binds a single scalar path) pass `false`.
   */
  showWildcardHint?: boolean;
  /** Override the trigger button's `data-testid`. */
  triggerTestId?: string;
  /** Override the freetext path input's `data-testid`. */
  inputTestId?: string;
  /** Forwarded to the trigger button's `id`; lets callers pair a `<label htmlFor>`. */
  triggerId?: string;
  /** Auto-open the popover on mount; focuses the freetext input. */
  autoFocusPath?: boolean;
  /** Fires after a list-pick + close so callers can move focus forward. */
  onPickPathComplete?: () => void;
  /** Disable the trigger (e.g. while a confirmation modal is pending). */
  disabled?: boolean;
};

/**
 * Path-discovery field shared by the JSON filter chip and the loose-FK
 * target picker. Owns the validator-hint computation and the
 * `JsonPeekPopover` composition so hosts only see a `value` /
 * `onChange` surface — no need to thread engine-specific validator
 * logic or peek-popover plumbing through every caller.
 *
 * Layout: the popover trigger + an inline `×` clear button when the
 * picker carries a non-empty selection (either a path OR a "whole
 * column" scope). The clear button is rendered as a sibling so the
 * popover trigger stays a single `<button role="combobox">` and Radix's
 * trigger semantics aren't broken by a nested actionable element.
 */
export function JsonPathField({
  value,
  onChange,
  onTreePick,
  onPickDocumentScope,
  scope = 'path',
  onClear,
  connectionId,
  database,
  schema,
  table,
  column,
  engine,
  declaredOnText = false,
  scanFilters,
  showWildcardHint = true,
  triggerTestId,
  inputTestId,
  triggerId,
  autoFocusPath,
  onPickPathComplete,
  disabled,
}: JsonPathFieldProps) {
  const { data: mssqlCompatLevel } = useMssqlCompatLevel(
    connectionId ?? null,
    database ?? null,
    engine,
  );
  const pathHint = useMemo(
    () => getJsonPathHint(value, { engine, mssqlCompatLevel }),
    [value, engine, mssqlCompatLevel],
  );
  // Wired only into the popover hint render — surfaces a tiny "Recheck"
  // button next to the red wildcard message when compat < 130, so the
  // user can pick up an externally-bumped compat level inline.
  const { recheck: handleRecheckCompat, isFetching: recheckCompatFetching } =
    useMssqlCompatRecheck(connectionId ?? null, database ?? null);
  // The `×` clear control only makes sense when the picker is actually
  // showing something the user could want to remove. Hide it on the
  // empty path-mode state so the picker still reads as a single-purpose
  // input. Skip when `onClear` isn't wired — the host doesn't want the
  // affordance (loose-FK target picker, for example).
  const showClear =
    onClear !== undefined && (scope === 'whole-column' || value !== '');

  return (
    <span className="inline-flex items-center gap-0.5">
      <JsonPeekPopover
        connectionId={connectionId}
        database={database}
        schema={schema}
        table={table}
        column={column}
        engine={engine}
        declaredOnText={declaredOnText}
        scanFilters={scanFilters}
        pathValue={value}
        pathHint={pathHint}
        autoFocusPath={autoFocusPath}
        onPathChange={onChange}
        onTreePick={onTreePick}
        onPickDocumentScope={onPickDocumentScope}
        onPickPathComplete={onPickPathComplete}
        disabled={disabled}
        scope={scope}
        showWildcardHint={showWildcardHint}
        triggerTestId={triggerTestId}
        inputTestId={inputTestId}
        triggerId={triggerId}
        onRecheckCompat={() => void handleRecheckCompat()}
        recheckCompatFetching={recheckCompatFetching}
      />
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          aria-label="Clear scope"
          title="Clear"
          data-testid="json-path-clear"
          className={cn(
            'inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}
